import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import { MAX_AUDIO_DURATION_SECONDS, MAX_AUDIO_DURATION_HOURS } from "../common/maxFileSize";
import {
  PAGE_SIZE,
  formatDuration,
  formatSize,
  getFileTypeIcon,
  MediaPlayer,
  SearchBar,
  Pagination,
  ModalListStates,
  DetailViewShell,
  ListViewShell,
  DetailField,
  ListItemButton,
} from "./MediaModalShared";

export interface EvidenceItem {
  id: string;
  title: string;
  externalId: string;
  recordedOn: string;
  uploadedOn: string;
  duration: number;
  fileType: string;
  status: string;
  sourceChannel: string;
  contentType: string;
  size: number;
  owner: string | null;
  ownerBadge: string | null;
}

interface EvidenceMeta {
  count: number;
  offset: number;
  limit: number;
}

interface EvidenceModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  onSelect: (evidence: EvidenceItem) => void;
  selectOnly?: boolean;
  selectedEvidenceIds?: string[];
}

const getDeviceLabel = (sourceChannel: string) => {
  const labels: Record<string, string> = {
    axonbody3: "Body 3",
    axonbody4: "Body 4",
    fleet3: "Fleet 3",
    fleet2: "Fleet 2",
    interview: "Interview",
  };
  return labels[sourceChannel] || sourceChannel;
};

const EvidenceModal: React.FC<EvidenceModalProps> = ({
  modalRef,
  onSelect,
  selectOnly = false,
  selectedEvidenceIds = [],
}) => {
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [meta, setMeta] = useState<EvidenceMeta>({
    count: 0,
    offset: 0,
    limit: PAGE_SIZE,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [selectedItem, setSelectedItem] = useState<EvidenceItem | null>(null);
  const [details, setDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchDetails = async (evidenceId: string) => {
    setDetailsLoading(true);
    setDetails(null);
    try {
      const res = await axios.get(`/api/evidence/${evidenceId}/details`);
      setDetails(res.data);
    } catch {
      setDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const [transferring, setTransferring] = useState(false);

  const handleSelectItem = (item: EvidenceItem) => {
    setSelectedItem(item);
    fetchDetails(item.id);
  };

  const handleTransfer = async (item: EvidenceItem) => {
    setTransferring(true);
    const toastId = toast.loading(`Transferring "${item.title || "evidence"}" to S3...`);

    try {
      const res = await axios.post(`/api/evidence/${item.id}/transfer`, {
        title: item.title,
      });
      toast.success(`Transfer complete: ${res.data.key}`, { id: toastId });
      onSelect(item);
      modalRef.current?.close();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Transfer failed",
        { id: toastId },
      );
    } finally {
      setTransferring(false);
    }
  };

  const fetchEvidence = async (offset: number, title?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        limit: String(PAGE_SIZE),
        offset: String(offset),
        status: "active",
        sort: "-recordedOn",
      };
      if (title) params.title = title;

      const res = await axios.get("/api/evidence", { params });
      setEvidence(res.data.evidence);
      setMeta(res.data.meta);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load evidence");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;

    const observer = new MutationObserver(() => {
      if (dialog.open) {
        setSelectedItem(null);
        if (evidence.length === 0) {
          fetchEvidence(0);
          setPage(0);
          setSearch("");
          setSearchInput("");
        }
      }
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, evidence.length]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
    fetchEvidence(0, searchInput || undefined);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchEvidence(newPage * PAGE_SIZE, search || undefined);
  };

  const totalPages = Math.ceil(meta.count / PAGE_SIZE);

  return (
    <dialog ref={modalRef} className="modal" data-tour="evidence-modal">
      {selectedItem ? (
        <DetailViewShell
          onBack={() => setSelectedItem(null)}
          onClose={() => {
            setSelectedItem(null);
            modalRef.current?.close();
          }}
          title={selectedItem.title || "Untitled"}
          headerMeta={
            <>
              {selectedItem.externalId && (
                <span>#{selectedItem.externalId}</span>
              )}
              <span>
                {new Date(selectedItem.recordedOn).toLocaleDateString()}{" "}
                {new Date(selectedItem.recordedOn).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              <span>{formatDuration(selectedItem.duration)}</span>
              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-base-200 rounded">
                {getDeviceLabel(selectedItem.sourceChannel)}
              </span>
              {selectedItem.owner && (
                <span>
                  {selectedItem.owner}
                  {selectedItem.ownerBadge && ` #${selectedItem.ownerBadge}`}
                </span>
              )}
            </>
          }
          media={
            <MediaPlayer
              src={`/api/evidence/${selectedItem.id}/stream`}
              isVideo={selectedItem.fileType === "video"}
            />
          }
          sidebar={
            <>
              {detailsLoading && (
                <div className="flex justify-center py-8">
                  <span className="loading loading-spinner loading-sm"></span>
                </div>
              )}
              {!detailsLoading && details && (
                <div className="space-y-3 text-sm">
                  {details.categories?.length > 0 && (
                    <DetailField label="Category">
                      <div className="flex flex-wrap gap-1">
                        {details.categories.map((cat: string, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </DetailField>
                  )}
                  {details.description && (
                    <DetailField label="Description">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">{details.description}</p>
                    </DetailField>
                  )}
                  {details.referenceNumber && (
                    <DetailField label="Reference #">
                      <p className="text-xs text-gray-700 dark:text-base-content/80 font-mono">{details.referenceNumber}</p>
                    </DetailField>
                  )}
                  {details.owner && (
                    <DetailField label="Owner">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">
                        {details.owner.firstName} {details.owner.lastName}
                        {details.owner.badgeNumber && (
                          <span className="text-base-content/40"> #{details.owner.badgeNumber}</span>
                        )}
                      </p>
                    </DetailField>
                  )}
                  {details.uploadedOn && (
                    <DetailField label="Uploaded">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">
                        {new Date(details.uploadedOn).toLocaleDateString()}{" "}
                        {new Date(details.uploadedOn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </DetailField>
                  )}
                  {details.tags?.length > 0 && (
                    <DetailField label="Tags">
                      <div className="flex flex-wrap gap-1">
                        {details.tags.map((tag: string, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-base-200 text-gray-600 dark:text-base-content/70 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </DetailField>
                  )}
                  {details.sensitive && (
                    <div className="flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 rounded text-xs font-medium">
                        Sensitive
                      </span>
                    </div>
                  )}
                  {details.deletionTime && (
                    <DetailField label="Retention Until">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">
                        {new Date(details.deletionTime).toLocaleDateString()}
                      </p>
                    </DetailField>
                  )}
                  {details.notes?.length > 0 && (
                    <DetailField label="Notes">
                      {details.notes.map((note: any, i: number) => (
                        <p key={i} className="text-xs text-gray-700 dark:text-base-content/80">
                          {typeof note === "string" ? note : note.text || JSON.stringify(note)}
                        </p>
                      ))}
                    </DetailField>
                  )}
                  {details.location && (
                    <DetailField label="Location">
                      <a
                        href={`https://www.google.com/maps?q=${details.location.latitude},${details.location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-mono"
                      >
                        {details.location.latitude.toFixed(5)}, {details.location.longitude.toFixed(5)}
                      </a>
                    </DetailField>
                  )}
                </div>
              )}
            </>
          }
          footerLeft={
            <>
              {formatSize(selectedItem.size)} &middot; {selectedItem.contentType}
            </>
          }
          footerRight={
            (() => {
              const isDisabled =
                selectedItem.duration > MAX_AUDIO_DURATION_SECONDS ||
                selectedEvidenceIds.includes(selectedItem.id) ||
                (!selectOnly && transferring);
              const buttonLabel =
                selectedItem.duration > MAX_AUDIO_DURATION_SECONDS
                  ? `Exceeds ${MAX_AUDIO_DURATION_HOURS}hr limit`
                  : selectedEvidenceIds.includes(selectedItem.id)
                    ? "Already added"
                    : selectOnly
                      ? "Add to Upload"
                      : transferring
                        ? "Transferring..."
                        : "Use This Evidence";
              return (
                <Button
                  onClick={() => {
                    if (selectOnly) {
                      onSelect(selectedItem);
                      setSelectedItem(null);
                      modalRef.current?.close();
                    } else {
                      handleTransfer(selectedItem);
                    }
                  }}
                  disabled={isDisabled}
                  variant="primary"
                  size="sm"
                  data-tour="evidence-add"
                >
                  {buttonLabel}
                </Button>
              );
            })()
          }
        />
      ) : (
        <ListViewShell
          title="Body Cam Footage"
          subtitle="Select evidence from Axon to import"
          onClose={() => modalRef.current?.close()}
        >
          <SearchBar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearch={handleSearch}
            placeholder="Search by title..."
          />

          <div className="flex-1 overflow-y-auto min-h-0">
            <ModalListStates
              loading={loading}
              error={error}
              isEmpty={evidence.length === 0}
              emptyMessage="No evidence found."
            />

            {!loading && !error && evidence.length > 0 && (
              <div className="space-y-2" data-tour="evidence-list">
                {evidence.map((item) => {
                  const exceedsDuration = item.duration > MAX_AUDIO_DURATION_SECONDS;
                  const alreadySelected = selectedEvidenceIds.includes(item.id);
                  const isDisabled = exceedsDuration || alreadySelected;
                  return (
                    <ListItemButton
                      key={item.id}
                      disabled={isDisabled}
                      onClick={() => handleSelectItem(item)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getFileTypeIcon(item.fileType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-base-content truncate">
                              {item.title || "Untitled"}
                            </h4>
                            {item.externalId && (
                              <span className="text-xs text-base-content/60 flex-shrink-0">
                                #{item.externalId}
                              </span>
                            )}
                            {alreadySelected && (
                              <span className="text-xs text-green-600 dark:text-green-400 flex-shrink-0 font-medium">
                                Added
                              </span>
                            )}
                            {exceedsDuration && !alreadySelected && (
                              <span className="text-xs text-red-500 flex-shrink-0">
                                Exceeds {MAX_AUDIO_DURATION_HOURS}hr limit
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-base-content/60">
                            <span>
                              {new Date(item.recordedOn).toLocaleDateString()}{" "}
                              {new Date(item.recordedOn).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            <span>{formatDuration(item.duration)}</span>
                            <span>{formatSize(item.size)}</span>
                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-base-200 rounded text-xs">
                              {getDeviceLabel(item.sourceChannel)}
                            </span>
                            {item.owner && (
                              <span>
                                {item.owner}
                                {item.ownerBadge && ` #${item.ownerBadge}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </ListItemButton>
                  );
                })}
              </div>
            )}
          </div>

          {!loading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={meta.count}
              onPageChange={handlePageChange}
            />
          )}
        </ListViewShell>
      )}
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
};

export default EvidenceModal;
